"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const react_native_1 = require("react-native");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const free_solid_svg_icons_1 = require("@fortawesome/free-solid-svg-icons");
const react_native_slider_1 = require("@miblanchard/react-native-slider");
const native_1 = require("@react-navigation/native");
const eventConstants_1 = require("../../constants/eventConstants");
const pressable_1 = __importDefault(require("../pressable/pressable"));
const styles_1 = __importDefault(require("../../styles/styles"));
const colors_1 = __importDefault(require("../../styles/colors"));
const useAppSelector_1 = __importDefault(require("../../hooks/useAppSelector"));
const communicationBoxUtils_1 = __importDefault(require("../communicationBox/communicationBoxUtils"));
const useWebDesktopDesign_1 = __importDefault(require("../../hooks/useWebDesktopDesign"));
const qualityAssuranceSlice_1 = require("../../features/qualityAssurance/qualityAssuranceSlice");
const useAppDispatch_1 = __importDefault(require("../../hooks/useAppDispatch"));
const audioComponent_1 = __importDefault(require("./audioComponent"));
const audioPlayerStyles_1 = __importDefault(require("./audioPlayerStyles"));
const DefaultProps = {
    multi: true,
};
const AudioPlayer = ({ multi }) => {
    const isDesktop = useWebDesktopDesign_1.default();
    const dispatch = useAppDispatch_1.default();
    const { callRecords, selectedCallRecordIndex } = useAppSelector_1.default((state) => ({
        callRecords: state.qualityAssurance.callRecords,
        selectedCallRecordIndex: state.qualityAssurance.selectedCallRecordIndex,
    }));
    const [trackProgress, setTrackProgress] = react_1.useState(0);
    const [isPlaying, setIsPlaying] = react_1.useState(false);
    const navigation = native_1.useNavigation();
    const flexDirection = isDesktop
        ? [styles_1.default.flexDirectionRow]
        : [styles_1.default.flexDirectionColumn, styles_1.default.alignCenterCenter];
    const callRecord = callRecords[selectedCallRecordIndex];
    const currentTime = typeof trackProgress === 'object' ? trackProgress[0] : trackProgress;
    // Set url to undefined since HTMLAudioElement can't accept null and this
    // component will only be mounted if url isn't null.
    const record = callRecord.record || undefined;
    const audioElement = react_1.useRef(new Audio(record));
    const intervalRef = react_1.useRef(0);
    const isReady = react_1.useRef(false);
    const { duration } = audioElement.current;
    const maxDurationSeconds = duration || 0;
    const currentDurationSeconds = trackProgress;
    const currentDuration = communicationBoxUtils_1.default(currentDurationSeconds, true);
    const maxDuration = communicationBoxUtils_1.default(maxDurationSeconds, true);
    // Stop audio from playing in the background.
    native_1.useFocusEffect(react_1.useCallback(() => {
        const unsubscribe = navigation.addListener('blur', () => {
            setIsPlaying(false);
        });
        return unsubscribe;
    }, [navigation]));
    const toPrevRecord = () => {
        const newIndex = selectedCallRecordIndex - 1;
        if (newIndex < 0) {
            dispatch(qualityAssuranceSlice_1.setSelectedCallRecordIndex(callRecords.length - 1));
        }
        else {
            dispatch(qualityAssuranceSlice_1.setSelectedCallRecordIndex(newIndex));
        }
        setTrackProgress(0);
    };
    const toNextRecord = () => {
        const newIndex = selectedCallRecordIndex + 1;
        if (newIndex <= callRecords.length - 1) {
            dispatch(qualityAssuranceSlice_1.setSelectedCallRecordIndex(newIndex));
        }
        else {
            dispatch(qualityAssuranceSlice_1.setSelectedCallRecordIndex(0));
        }
        setTrackProgress(0);
    };
    const startTimer = () => {
        // Clear any timers already running
        clearInterval(intervalRef.current);
        intervalRef.current = setInterval(() => {
            const isEnded = !!(audioElement.current && audioElement.current.ended);
            if (isEnded && multi) {
                toNextRecord();
            }
            else if (isEnded && !multi) {
                audioElement.current.currentTime = 0;
                setTrackProgress(0);
                setIsPlaying(false);
            }
            else {
                setTrackProgress(audioElement.current.currentTime || 0);
            }
        }, [1000]);
        return () => {
            clearInterval(intervalRef.current);
        };
    };
    react_1.useEffect(() => {
        if (isPlaying) {
            audioElement.current.currentTime = currentTime;
            audioElement.current.play();
            startTimer();
        }
        else {
            clearInterval(intervalRef.current);
            audioElement.current.pause();
        }
    }, [isPlaying]);
    // Handle setup when changing tracks
    react_1.useEffect(() => {
        audioElement.current.pause();
        audioElement.current = new Audio(record);
        setTrackProgress(audioElement.current.currentTime);
        if (isReady.current) {
            audioElement.current.currentTime = currentTime;
            audioElement.current.play();
            setIsPlaying(true);
            startTimer();
        }
        else {
            // Set the isReady ref as true for the next pass
            isReady.current = true;
        }
        // Store the audio state. It will not be present in unmount
        // for cleanup
        const localAudioRef = audioElement.current;
        return () => {
            // Clean up on unmount.
            if (localAudioRef) {
                localAudioRef.pause();
            }
            clearInterval(intervalRef.current);
        };
    }, [selectedCallRecordIndex]);
    const playHandler = () => {
        setIsPlaying(!isPlaying);
    };
    const skipHandler = (value) => {
        audioElement.current.currentTime = typeof value === 'number' ? value : value[0];
        setTrackProgress(value);
    };
    return (react_1.default.createElement(react_native_1.View, { style: [...flexDirection, styles_1.default.flexOne] },
        react_1.default.createElement(react_native_1.View, { style: [styles_1.default.flexDirectionRow, styles_1.default.flexOne] },
            react_1.default.createElement(react_native_1.View, { style: [styles_1.default.alignCenterCenter, styles_1.default.marginTen, audioPlayerStyles_1.default.icon] },
                react_1.default.createElement(react_native_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faMicrophone, size: 75, color: colors_1.default.WHITE })),
            react_1.default.createElement(react_native_1.View, { style: [
                    styles_1.default.marginTen,
                    styles_1.default.alignCenterStart,
                    !isDesktop && audioPlayerStyles_1.default.maxWidthTwoHundred,
                ] },
                react_1.default.createElement(react_native_1.Text, { style: audioPlayerStyles_1.default.title }, callRecord.officer),
                react_1.default.createElement(react_native_1.Text, { style: audioPlayerStyles_1.default.subTitle }, callRecord.id))),
        react_1.default.createElement(react_native_1.View, { style: [flexDirection, styles_1.default.flexOne, styles_1.default.alignCenterStart] },
            react_1.default.createElement(react_native_1.View, { style: [styles_1.default.flexOne, styles_1.default.alignCenterCenter, styles_1.default.flexDirectionRow] },
                react_1.default.createElement(pressable_1.default, { onPress: toPrevRecord, disabled: !multi, eventName: eventConstants_1.PLAY_CALL_DETAIL_RECORD, style: [styles_1.default.marginFive] },
                    react_1.default.createElement(react_native_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faBackward, size: 40, color: multi ? colors_1.default.BLUE_2 : colors_1.default.WHITE })),
                react_1.default.createElement(pressable_1.default, { onPress: playHandler, eventName: eventConstants_1.PLAY_CALL_DETAIL_RECORD, style: [
                        styles_1.default.roundedIconContainer,
                        styles_1.default.blueBackground_2,
                        styles_1.default.marginFive,
                        styles_1.default.alignCenterCenter,
                    ] },
                    react_1.default.createElement(react_native_fontawesome_1.FontAwesomeIcon, { icon: isPlaying ? free_solid_svg_icons_1.faPause : free_solid_svg_icons_1.faPlay, size: 30, color: colors_1.default.WHITE })),
                react_1.default.createElement(pressable_1.default, { disabled: !multi, onPress: toNextRecord, eventName: eventConstants_1.PLAY_CALL_DETAIL_RECORD, style: [styles_1.default.marginFive] },
                    react_1.default.createElement(react_native_fontawesome_1.FontAwesomeIcon, { icon: free_solid_svg_icons_1.faForward, size: 40, color: multi ? colors_1.default.BLUE_2 : colors_1.default.WHITE }))),
            react_1.default.createElement(react_native_1.View, { style: [
                    styles_1.default.flexTwo,
                    styles_1.default.alignCenterCenter,
                    styles_1.default.flexDirectionRow,
                    !isDesktop && audioPlayerStyles_1.default.maxWidthFifty,
                ] },
                react_1.default.createElement(react_native_1.Text, { style: styles_1.default.marginTen }, currentDuration),
                react_1.default.createElement(react_native_slider_1.Slider, { value: trackProgress, onValueChange: skipHandler, maximumValue: maxDurationSeconds, thumbTintColor: colors_1.default.BLUE_2, trackStyle: styles_1.default.whiteBackground, minimumTrackTintColor: colors_1.default.BLUE_2, containerStyle: audioPlayerStyles_1.default.minWidthTwoFifty }),
                react_1.default.createElement(react_native_1.Text, { style: styles_1.default.marginTen }, maxDuration),
                react_1.default.createElement(audioComponent_1.default, { audioElement: audioElement, label: callRecord.id })))));
};
AudioPlayer.defaultProps = DefaultProps;
exports.default = AudioPlayer;
